import { yupResolver } from '@hookform/resolvers/yup'
import { FormHelperText, Stack } from '@mui/material'
import { useCallback, useEffect } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import useAccessTokenAndShopId from '../../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { useSelectPricingMatrix } from '../../../hooks/useSelectPricingMatrix/useSelectPricingMatrix'
import { DiscreteUnitString } from '../../../models/convertUnitOfMeasureEnum'
import { LabelsPartsInfo } from '../../../models/Labels'
import {
  useCreateCannedJobToInventoryPartMutation,
  useUpdateCannedJobToInventoryPartMutation
} from '../../../redux/api/cannedJobApi'
import {
  CannedJobInventoryPartEntry,
  CannedJobInventoryPartInfo,
  CannedJobInventoryPartInfoUnitOfMeasureEnum,
  InventoryPartEntry,
  ShopInventory
} from '../../../sdk'
import {
  getDefaultUnitForInventoryPart,
  getPartAvailableQuantity
} from '../../../utils/convertFluidsUnits/convertFluidsUnits'
import {
  ControlledMuiCheckboxSingle,
  ControlledMuiSelect,
  ControlledMuiTextField
} from '../../FormField'
import { VALIDATE_SEARCH_INVENTORY_FOR_CANNEDJOB } from '../validation'

export const CannedJobInventoryForm = ({
  existingEntry,
  cannedJobId,
  selected,
  shopInventory,
  submitCallback
}: {
  existingEntry: CannedJobInventoryPartEntry | undefined
  cannedJobId: string
  selected: InventoryPartEntry
  shopInventory: ShopInventory
  submitCallback: () => void
}) => {
  const { handleSubmit, control, setValue, watch } =
    useForm<CannedJobInventoryPartInfo>({
      mode: 'onBlur',
      defaultValues: existingEntry
        ? existingEntry.info
        : {
            quantity: 1,
            cannedJob: cannedJobId,
            partsMatrix: undefined,
            unitOfMeasure: getDefaultUnitForInventoryPart(selected),
            taxExempt: false
          },
      resolver: yupResolver(VALIDATE_SEARCH_INVENTORY_FOR_CANNEDJOB)
    })
  const validUnits = selected.info!.isFluids
    ? Object.values(CannedJobInventoryPartInfoUnitOfMeasureEnum).filter(
        (unit) => String(unit) !== DiscreteUnitString
      )
    : [CannedJobInventoryPartInfoUnitOfMeasureEnum.DiscreteUnit]
  const currentUnit = watch('unitOfMeasure')
  const currentQuantity = watch('quantity')
  const availableUnits = getPartAvailableQuantity(
    selected.id!,
    shopInventory,
    currentUnit
  )
  const {
    selectedPartsMatrixId,
    SelectPriceLevelComponent,
    SelectPartsMatrixComponent
  } = useSelectPricingMatrix(
    '',
    '',
    existingEntry?.info?.partsMatrix || selected?.info?.partsMatrix || ''
  )
  useEffect(() => {
    setValue('partsMatrix', selectedPartsMatrixId)
  }, [selectedPartsMatrixId, setValue])
  const { accessToken } = useAccessTokenAndShopId()

  const [createCannedJobInventory] = useCreateCannedJobToInventoryPartMutation()
  const [updateCannedJobInventory] = useUpdateCannedJobToInventoryPartMutation()
  const onSubmit: SubmitHandler<CannedJobInventoryPartInfo> = useCallback(
    async (data) => {
      if (existingEntry) {
        updateCannedJobInventory({
          authorization: accessToken,
          cannedJobInventoryPartEntry: {
            id: existingEntry.id!,
            info: {
              ...existingEntry.info,
              ...data
            }
          }
        })
      } else {
        createCannedJobInventory({
          authorization: accessToken,
          cannedJobInventoryPartInfo: {
            ...data,
            quantity: data.quantity || 0
          }
        })
      }
      submitCallback()
    },
    [
      existingEntry,
      submitCallback,
      updateCannedJobInventory,
      accessToken,
      createCannedJobInventory
    ]
  )
  return (
    <form
      id={'searchInventoryForm'}
      onSubmit={handleSubmit((data) => {
        onSubmit({ ...data, inventoryPart: selected?.id! })
      })}>
      <Stack direction="row" justifyContent="space-between" spacing={2} p={1}>
        <Stack>
          {' '}
          <ControlledMuiTextField
            variant="standard"
            name="quantity"
            label={LabelsPartsInfo.quantity + ` (${availableUnits} available)`}
            control={control}
            fullWidth
          />
          {currentQuantity !== undefined &&
            currentQuantity > availableUnits && (
              <FormHelperText
                error>{`Only ${availableUnits} available`}</FormHelperText>
            )}
        </Stack>

        <ControlledMuiSelect
          variant="standard"
          name="unitOfMeasure"
          control={control}
          options={validUnits.map((u) => ({
            id: u,
            description: u
          }))}
          label={LabelsPartsInfo.unitOfMeasure}
          fullWidth
        />
        <SelectPriceLevelComponent variant="standard" />
        <SelectPartsMatrixComponent variant="standard" customNoneText="" />
      </Stack>
      <ControlledMuiCheckboxSingle
        sx={{ ml: 2 }}
        control={control}
        name="taxExempt"
        label={LabelsPartsInfo.taxExempt}
      />
    </form>
  )
}
